import Pusher from 'pusher-js';
import Channel from 'pusher-js/types/src/core/channels/channel';

import { PusherConfig } from '@/store/useSettingsStore';
import { App } from 'vue';

import { useDebug, DEBUG_NAMESPACE } from '@/composables/useDebug';

const ITEM_CHANNEL_TEMPLATE: string = 'item_{{itemId}}_v5';

type EventCallback = (data: any) => void;
const { debug } = useDebug(DEBUG_NAMESPACE.PUSHER);

Pusher.log = message => debug(message);

export class PusherPlugin {
  private _instance: Pusher;
  private _config: PusherConfig;

  public userChannel: Channel | null = null;
  public itemChannel: Channel | null = null;

  constructor(config: PusherConfig) {
    this._config = config;
    this._instance = new Pusher(this._config.apiKey, {
      forceTLS: true,
      authEndpoint: this._config.authUrl,
    });

    if (this._config.channel) {
      this.userChannel = this._instance.subscribe(this._config.channel);
    }
  }

  public bindUser(eventName: string, callback: EventCallback) {
    if (!this.userChannel) {
      console.error('No user channel found. Please subscribe to a user first.');
      return;
    }
    this.userChannel.bind(eventName, callback);
  }

  public unbindUser(event: string, callback?: EventCallback) {
    this.userChannel?.unbind(event, callback);
  }

  public bindItem(eventName: string, callback: EventCallback) {
    if (!this.itemChannel) {
      console.error('No item channel found. Please subscribe to an item first.');
      return;
    }

    this.itemChannel.bind(eventName, callback);
  }

  public unbindItem(event: string, callback?: EventCallback) {
    this.itemChannel?.unbind(event, callback);
  }

  public unbindAllItemChannel() {
    this.itemChannel?.unbind();
  }

  public subscribeToItem(itemId: string) {
    this.itemChannel = this._instance.subscribe(ITEM_CHANNEL_TEMPLATE.replace('{{itemId}}', itemId));
  }

  public unsubscribeFromItem() {
    if (!this.itemChannel) {
      return;
    }

    this.itemChannel.unsubscribe();
  }
}

export const createPusherPlugin = {
  install(app: App, pusherConfig: PusherConfig) {
    const pusher = new PusherPlugin(pusherConfig);

    app.provide('$pusher', pusher);
  },
};
